import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { AppLayoutComponent } from '@shared/layout/app-layout/app-layout.component';
import { authGuard } from '@shared/auth/guards/auth.guard';
import { redirectAuthenticatedGuard } from '@shared/auth/guards/redirect-authenticated.guard';
import { adminGuard } from '@shared/auth/guards/admin.guard';
import { permissionGuard, requireActionGuard } from '@shared/auth/guards/permission.guard';
import { transferenciaTokenGuard } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-token.guard';
import { nexusScope } from '@models/i18n/nexus-scope';
import { transferenciaConfirmarResolver } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.resolver';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'inicio',
        loadComponent: () => import('@partnercore/feature-home/inicio/inicio.component').then(m => m.InicioComponent),
        title: 'titles.inicio',
      },
      {
        path: 'notifications',
        loadComponent: () => import('@partnercore/feature-modules/modulos/control-general/notificaciones/notifications/notifications.component').then(m => m.NotificationsComponent),
        title: 'titles.notificaciones',
      },
      {
        path: 'configuracion',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/configuracion.component').then(m => m.ConfiguracionComponent),
        title: 'titles.configuracion',
      },
      {
        path: 'soporte',
        loadComponent: () => import('@partnercore/feature-settings/soporte/soporte.component').then(m => m.SoporteComponent),
        title: 'titles.soporte',
      },
      {
        path: 'configuracion/transferir',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.component').then(m => m.TransferenciaConfirmarComponent),
        title: 'titles.confirmTransfer',
        canActivate: [transferenciaTokenGuard],
        resolve: { transferenciaInfo: transferenciaConfirmarResolver },
      },
      // Dashboard Sistema
      {
        path: 'dashboard-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard-general',
            pathMatch: 'full',
          },
          {
            path: 'dashboard-general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Admin Sistema
      {
        path: 'admin-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuariosSistema',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisosSistema',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisosSistema',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesSistema',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfilSistema',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresasSistema',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
          {
            path: 'clientes',
            loadComponent: () => import('@partnercore/feature-admin/clientes/clientes.component').then(m => m.ClientesComponent),
            canActivate: [permissionGuard],
            title: 'titles.clientesSistema',
          },
          {
            path: 'planes',
            loadComponent: () => import('@partnercore/feature-admin/planes/planes.component').then(m => m.PlanesComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'planes/:id',
            loadComponent: () => import('@partnercore/feature-admin/planes/components/plan-detail/plan-detail.component').then(m => m.PlanDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'productos',
            loadComponent: () => import('@partnercore/feature-admin/productos/productos.component').then(m => m.ProductosComponent),
            canActivate: [permissionGuard],
            title: 'titles.productosSistema',
          },
        ],
      },
      // Seguridad Sistema (SYS) — ruta top-level para que coincida con la URL sembrada en DB: /seguridad-sistema/*
      {
        path: 'seguridad-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.sesionesSistema',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.auditSistema',
          },
        ],
      },
      // Dashboard Control General
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: 'general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Administrativos
      {
        path: 'admin',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuarios',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisos',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisos',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesAdmin',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfil',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresas',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
        ],
      },
      // Estructura
      {
        path: 'estructura-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'modulos-sistema',
            pathMatch: 'full',
          },
          {
            path: 'modulos-sistema',
            loadComponent: () => import('@partnercore/feature-modules/modulos/module.component').then(m => m.ModuleComponent),
            canActivate: [permissionGuard],
            title: 'titles.modulosSistema',
          },
        ],
      },
      // Maestros Sistema
      {
        path: 'maestros-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedasSistema',
          },
        ],
      },
      // Maestros
      {
        path: 'maestros',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedas',
          },
        ],
      },
      // Seguridad Control General — ruta top-level para que coincida con la URL sembrada en DB: /seguridad/*
      {
        path: 'seguridad',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.sesiones',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.audit',
          },
        ],
      },

      // ═══════════════════════════════════════════════════════════════
      // SMART TI — Módulos de Gestión de Proyectos y Productividad
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'nexus',
        canActivate: [permissionGuard],
        // Diccionario de NEXUS: se carga al entrar en cualquiera de sus módulos y lo heredan todas
        // las rutas hijas. Vive fuera del JSON global para no sumar ~3.500 claves a la descarga
        // inicial de quien sólo trabaja con PartnerCore. Ver `nexusScope()`.
        providers: [nexusScope()],
        children: [
          // M01 — Tareas y Espacios
          //
          // La ruta 'espacios' se retiró como destino propio: la jerarquía
          // Espacio → Subespacio → Lista es el árbol de navegación DENTRO de esta
          // pantalla, no un sitio aparte. Tenerla separada obligaba a cambiar de
          // vista para crear una lista y volver para ver sus tareas.
          //
          // Se deja una redirección —no se borra la ruta— para no romper enlaces
          // guardados, marcadores ni el historial de quien ya usaba /nexus/espacios.
          // M01 — «Espacios»: alta, detalle y configuración. La ruta vuelve a tener pantalla
          // propia, pero con un alcance distinto del que V1001 retiró: allí se llevaba la
          // navegación Espacio → Carpeta → Lista y obligaba a saltar de pantalla para crear una
          // lista; aquí sólo vive la administración y el árbol se queda íntegro en «Tareas».
          {
            path: 'espacios',
            loadComponent: () => import('@nexus/feature-spaces/spaces/spaces.component').then(m => m.SpacesComponent),
            canActivate: [permissionGuard],
            title: 'titles.espacios',
          },
          {
            path: 'tareas',
            loadComponent: () => import('@nexus/feature-tasks/tasks/tasks.component').then(m => m.TasksComponent),
            canActivate: [permissionGuard],
            title: 'titles.tareas',
            // Pantalla de ancho completo: no es un documento que convenga centrar y estrechar,
            // sino una aplicación con su árbol fijo a la izquierda, y ya sangra hasta los bordes
            // del contenedor. Ver `AppLayoutComponent.anchoCompleto` (INC-0024).
            data: { anchoCompleto: true },
          },
          // M02 — Portafolios
          {
            path: 'portafolios',
            loadComponent: () => import('@nexus/feature-portfolios/portfolios/portfolios.component').then(m => m.PortfoliosComponent),
            canActivate: [permissionGuard],
            title: 'titles.portafolios',
          },
          {
            path: 'portafolios/:portfolioId',
            loadComponent: () => import('@nexus/feature-portfolios/portfolio-detail/portfolio-detail.component').then(m => m.PortfolioDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.portafolioDetalle',
          },
          // M04 — Equipos y Usuarios
          {
            path: 'equipos',
            loadComponent: () => import('@nexus/feature-teams/equipos-usuarios/equipos-usuarios.component').then(m => m.EquiposUsuariosComponent),
            canActivate: [permissionGuard],
            title: 'titles.equipos',
          },
          // M06 — Conocimiento y DAM (Wiki, Pizarras, Repositorio, Aprobación de Arte, Componentes
          // Vivos, Bases de Conocimiento: pestañas internas, un único permiso de módulo)
          {
            path: 'wiki',
            loadComponent: () => import('@nexus/feature-wiki/conocimiento/conocimiento.component').then(m => m.ConocimientoComponent),
            canActivate: [permissionGuard],
            title: 'Conocimiento y DAM - SMART TI',
          },
          // M07 — Control de tiempo
          {
            path: 'tiempo',
            loadComponent: () => import('@nexus/feature-time/time-tracking/time-tracking.component').then(m => m.TimeTrackingComponent),
            canActivate: [permissionGuard],
            title: 'Productividad y Tiempo - SMART TI',
          },
          // M05 — Colaboración y Comunicación
          {
            path: 'colaboracion',
            loadComponent: () => import('@nexus/feature-collaboration/colaboracion-comunicacion/colaboracion-comunicacion.component').then(m => m.ColaboracionComunicacionComponent),
            canActivate: [permissionGuard],
            title: 'Colaboración - SMART TI',
          },
          // M08 — OKR
          {
            path: 'okr',
            loadComponent: () => import('@nexus/feature-okr/okr-tracking/okr-tracking.component').then(m => m.OkrTrackingComponent),
            canActivate: [permissionGuard],
            title: 'OKR - SMART TI',
          },
          // M13 — CRM
          {
            path: 'crm',
            loadComponent: () => import('@nexus/feature-crm/crm/crm.component').then(m => m.CrmComponent),
            canActivate: [permissionGuard],
            title: 'CRM - SMART TI',
          },
          // M14 — Helpdesk
          {
            path: 'helpdesk',
            loadComponent: () => import('@nexus/feature-helpdesk/helpdesk/helpdesk.component').then(m => m.HelpdeskComponent),
            canActivate: [permissionGuard],
            title: 'titles.helpdesk',
          },
          // M23 — Facturación
          {
            path: 'facturacion',
            loadComponent: () => import('@nexus/feature-billing/billing/billing.component').then(m => m.BillingComponent),
            canActivate: [permissionGuard],
            title: 'Facturación y Suscripción - SMART TI',
          },
          // M12 — Financiero
          {
            path: 'financiero',
            loadComponent: () => import('@nexus/feature-financials/financials/financials.component').then(m => m.FinancialsComponent),
            canActivate: [permissionGuard],
            title: 'titles.financiero',
          },
          // M10 — Automatización e IA (hub con pestañas: Automatizaciones / Copilot IA / Centro de Plantillas)
          {
            path: 'automatizacion',
            loadComponent: () => import('@nexus/feature-automation/automation-hub/automation-hub.component').then(m => m.AutomationHubComponent),
            canActivate: [permissionGuard],
            title: 'Automatización e IA - SMART TI',
          },
          // M11 — Reportes
          {
            path: 'reportes',
            loadComponent: () => import('@nexus/feature-reports/reports/reports.component').then(m => m.ReportsComponent),
            canActivate: [permissionGuard],
            title: 'Reportes - SMART TI',
          },
          // M15 — DevOps
          {
            path: 'devops',
            loadComponent: () => import('@nexus/feature-devops/devops/devops.component').then(m => m.DevOpsComponent),
            canActivate: [permissionGuard],
            title: 'DevOps - SMART TI',
          },
          // M15.05 RF-001: aquí devuelve el proveedor Git al Admin tras autorizar la App. Va
          // antes que 'devops' en profundidad pero después en la lista porque Angular casa por
          // ruta completa; y va dentro del layout autenticado a propósito: el canje del código lo
          // hace la sesión del Admin, no una ruta pública.
          {
            path: 'devops/oauth/callback',
            loadComponent: () => import('@nexus/feature-devops/devops/components/oauth-callback/oauth-callback.component')
              .then(m => m.OauthCallbackComponent),
            canActivate: [permissionGuard],
            title: 'DevOps - SMART TI',
          },
          // M18 — Reservas
          {
            path: 'reservas',
            loadComponent: () => import('@nexus/feature-reservations/reservations/reservations.component').then(m => m.ReservationsComponent),
            canActivate: [permissionGuard],
            title: 'Reservas - SMART TI',
          },
          // M20 — ITSM
          {
            path: 'itsm',
            loadComponent: () => import('@nexus/feature-itsm/itsm/itsm.component').then(m => m.ItsmComponent),
            canActivate: [permissionGuard],
            title: 'ITSM - SMART TI',
          },
          // M17 — Mensajería / Chat
          {
            path: 'mensajes',
            loadComponent: () => import('@nexus/feature-messaging/messaging/messaging.component').then(m => m.MessagingComponent),
            canActivate: [permissionGuard],
            title: 'titles.chatDeProyecto',
          },
          // M21 — Integraciones
          {
            path: 'integraciones',
            loadComponent: () => import('@nexus/feature-integrations/integrations/integrations.component').then(m => m.IntegrationsComponent),
            canActivate: [permissionGuard],
            title: 'Integraciones - SMART TI',
          },
          // M19 — Sandbox y Entornos de Despliegue
          {
            path: 'sandbox',
            loadComponent: () => import('@nexus/feature-sandbox/sandbox/sandbox.component').then(m => m.SandboxComponent),
            canActivate: [permissionGuard],
            title: 'Sandbox - SMART TI',
          },
          // M24 — Configuración PWA / Móvil
          {
            path: 'pwa',
            loadComponent: () => import('@nexus/feature-pwa/pwa/pwa-settings.component').then(m => m.PwaSettingsComponent),
            canActivate: [permissionGuard],
            title: 'Configuración Móvil - SMART TI',
          },
          // M03 — Visualizaciones y Vistas
          {
            path: 'visualizaciones',
            loadComponent: () => import('@nexus/feature-visualizations/visualizations/visualizations.component').then(m => m.VisualizationsComponent),
            canActivate: [permissionGuard],
            title: 'titles.visualizacion',
          },
          // M09 — Personalización
          {
            path: 'personalizacion',
            loadComponent: () => import('@nexus/feature-customfields/customization/customization.component').then(m => m.CustomizationComponent),
            canActivate: [permissionGuard],
            title: 'Personalización - SMART TI',
          },
          // M16 — Seguridad y Auditoría (NEXUS scope)
          {
            path: 'seguridad-nexus',
            loadComponent: () => import('@nexus/feature-collaboration/security/nexus-security.component').then(m => m.NexusSecurityComponent),
            canActivate: [permissionGuard],
            title: 'Seguridad y Auditoría - SMART TI',
          },
          // M22 — Onboarding de Tenant
          {
            path: 'onboarding',
            loadComponent: () => import('@nexus/feature-sandbox/onboarding/onboarding.component').then(m => m.OnboardingComponent),
            canActivate: [permissionGuard],
            title: 'Onboarding - SMART TI',
          },
          // M25 — Gamificación, Adopción y Cultura (25.1 Celebraciones, 25.2 Logros y Badges,
          // 25.3 Centro de Adopción, 25.4 Portal de Bienestar — como pestañas internas, una sola
          // entrada de catálogo, mismo patrón que Mensajería/M17).
          {
            path: 'gamificacion',
            loadComponent: () => import('@nexus/feature-gamification/gamificacion-adopcion.component').then(m => m.GamificacionAdopcionComponent),
            canActivate: [permissionGuard],
            title: 'Gamificación y Adopción - SMART TI',
          },
          // M26.3 — Hub de Entregables (lado PM). El portal del cliente vive fuera del layout
          // autenticado, junto a los demás enlaces "portal/webinar/canales-externos" más abajo.
          {
            path: 'entregables',
            loadComponent: () => import('@nexus/feature-deliverables/pm/deliverables.component').then(m => m.DeliverablesComponent),
            canActivate: [permissionGuard],
            title: 'Hub de Entregables - SMART TI',
          },
          // M26.2 — Compartición Selectiva (lado PM). El portal del cliente vive fuera del
          // layout autenticado, junto a Entregables/webinar/canales-externos más abajo.
          {
            path: 'paginas-compartidas',
            loadComponent: () => import('@nexus/feature-shared-pages/pm/shared-pages.component').then(m => m.SharedPagesComponent),
            canActivate: [permissionGuard],
            title: 'Compartición Selectiva - SMART TI',
          },
          // M26.1 — Sitios de Marca Blanca. Sin portal de cliente propio en esta versión: no hay
          // enrutamiento por dominio real todavía (ver nota de alcance en el backend), así que es
          // solo pantalla de configuración de Admin.
          {
            path: 'marca-blanca',
            loadComponent: () => import('@nexus/feature-whitelabel/whitelabel-sites.component').then(m => m.WhitelabelSitesComponent),
            canActivate: [permissionGuard],
            title: 'Sitios de Marca Blanca - SMART TI',
          },
          // M27.1 — Archivador Automatizado: candidatos a archivado, proyectos archivados y la
          // política de umbral/gracia del tenant. El archivado/reactivación manual reutiliza
          // SpaceService (ya existente); esta pantalla es la vista dedicada que pedía RF-007.
          {
            path: 'archivador',
            loadComponent: () => import('@nexus/feature-archiver/proyectos-archivados.component').then(m => m.ProyectosArchivadosComponent),
            canActivate: [permissionGuard],
            title: 'Proyectos Archivados - SMART TI',
          },
          // M27.2 — Reglas de Retención de Evidencias: elimina/enfría archivos pesados de
          // proyectos archivados tras un período configurable, con ventana de exclusión manual.
          {
            path: 'retencion-evidencias',
            loadComponent: () => import('@nexus/feature-retention/reglas-retencion.component').then(m => m.ReglasRetencionComponent),
            canActivate: [permissionGuard],
            title: 'Retención de Evidencias - SMART TI',
          },
          // M27.3 — Duplicador de Plantillas Maestro: candidatos → preview → aplicar. El botón
          // "Propagar cambios" de Plantillas de Proyecto (M10.3) navega aquí con ?templateId=X.
          {
            path: 'propagacion-plantillas',
            loadComponent: () => import('@nexus/feature-template-propagation/duplicador-plantillas.component').then(m => m.DuplicadorPlantillasComponent),
            canActivate: [permissionGuard],
            title: 'Duplicador de Plantillas - SMART TI',
          },
          // M28.1 — Diseñador de Interfaces Personalizadas (Low-Code Builder): catálogo de apps
          // internas, editor de pantalla completa, y el runtime de uso final (web/PWA responsiva).
          {
            path: 'low-code-builder',
            loadComponent: () => import('@nexus/feature-lowcode-builder/catalogo-aplicaciones.component').then(m => m.CatalogoAplicacionesComponent),
            canActivate: [permissionGuard],
            title: 'Diseñador de Interfaces - SMART TI',
          },
          {
            path: 'low-code-builder/:id/editar',
            loadComponent: () => import('@nexus/feature-lowcode-builder/builder/app-builder.component').then(m => m.AppBuilderComponent),
            canActivate: [permissionGuard],
            title: 'Editar aplicación - SMART TI',
          },
          // RUTAS DE RUNTIME bajo el mismo prefijo del módulo a propósito: `permissionGuard`
          // valida por "empieza con la URL del módulo" (ver permission.guard.ts), y no existe un
          // módulo separado para `/nexus/apps` — sería invisible para el guard y siempre 404.
          {
            path: 'low-code-builder/apps/:id',
            loadComponent: () => import('@nexus/feature-lowcode-builder/runtime/app-runtime.component').then(m => m.AppRuntimeComponent),
            canActivate: [permissionGuard],
            title: 'Aplicación interna - SMART TI',
          },
          {
            path: 'low-code-builder/apps/:id/solicitudes',
            loadComponent: () => import('@nexus/feature-lowcode-builder/runtime/submissions-list.component').then(m => m.SubmissionsListComponent),
            canActivate: [permissionGuard],
            title: 'Solicitudes - SMART TI',
          },
          // M28.2 — Motor de Extensiones: catálogo, editor (código/disparador/límites/prueba/
          // ejecuciones) y administración de developers/lista blanca de dominios. Mismo motivo que
          // M28.1 arriba para mantener todo bajo el prefijo `motor-extensiones` (permissionGuard).
          {
            path: 'motor-extensiones',
            loadComponent: () => import('@nexus/feature-extensions/catalogo-extensiones.component').then(m => m.CatalogoExtensionesComponent),
            canActivate: [permissionGuard],
            title: 'Motor de Extensiones - SMART TI',
          },
          {
            path: 'motor-extensiones/admin',
            loadComponent: () => import('@nexus/feature-extensions/admin/extension-admin.component').then(m => m.ExtensionAdminComponent),
            canActivate: [permissionGuard],
            title: 'Developers y dominios - SMART TI',
          },
          {
            path: 'motor-extensiones/:id',
            loadComponent: () => import('@nexus/feature-extensions/editor/extension-editor.component').then(m => m.ExtensionEditorComponent),
            canActivate: [permissionGuard],
            title: 'Editar extensión - SMART TI',
          },
          // M28.3 — Generador de Portadas Dinámicas: catálogo (solo Owner/Admin) + editor de
          // cuadrícula de widgets + preview de solo lectura. La resolución automática (GET /me)
          // ya NO se pinta en el home genérico de PartnerCore (`InicioComponent`, ese es un
          // panel de administración de cuenta/seguridad de PartnerCore — Roles, Permisos,
          // Empresas, Auditoría — no una pantalla de SmartTi). `PortadaHomeComponent` queda sin
          // usar hasta que exista una landing propia de SmartTi/Nexus donde corresponda montarla.
          {
            path: 'generador-portadas',
            loadComponent: () => import('@nexus/feature-dashboard-layouts/catalogo-portadas.component').then(m => m.CatalogoPortadasComponent),
            canActivate: [permissionGuard],
            title: 'Generador de Portadas - SMART TI',
          },
          {
            path: 'generador-portadas/:id/editar',
            loadComponent: () => import('@nexus/feature-dashboard-layouts/builder/portada-builder.component').then(m => m.PortadaBuilderComponent),
            canActivate: [permissionGuard],
            title: 'Editar Portada - SMART TI',
          },
          {
            path: 'generador-portadas/:id/preview',
            loadComponent: () => import('@nexus/feature-dashboard-layouts/preview/portada-preview.component').then(m => m.PortadaPreviewComponent),
            canActivate: [permissionGuard],
            title: 'Vista previa de Portada - SMART TI',
          },
          // La papelera no tiene ruta propia: vive dentro de «Tareas y Espacios», junto al botón
          // de Archivados, porque lo que restaura o purga es la misma jerarquía que esa pantalla
          // navega. Un submódulo aparte duplicaba la entrada en el sidebar y dejaba al usuario en
          // una página sin el árbol al que devolver lo restaurado.
          // M01 — Task Detail full page
          {
            path: 'tareas/:id',
            loadComponent: () => import('@nexus/feature-tasks/tasks/components/task-detail-page/task-detail-page.component').then(m => m.TaskDetailPageComponent),
            canActivate: [permissionGuard],
            title: 'Detalle de Tarea - SMART TI',
          },
          // M01 — Task History / Audit
          {
            path: 'tareas/:id/historial',
            loadComponent: () => import('@nexus/feature-tasks/tasks/components/task-history/task-history.component').then(m => m.TaskHistoryComponent),
            canActivate: [permissionGuard],
            title: 'Historial de Tarea - SMART TI',
          },
        ],
      },
    ],
  },
  // Auth
  {
    path: 'auth',
    loadComponent: () => import('@partnercore/feature-auth-pages/auth/auth.component').then(m => m.AuthComponent),
    canActivate: [redirectAuthenticatedGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/login/login.component-page').then(m => m.LoginPageComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/forgot-password/forgot-password-page.component').then(m => m.ForgotPasswordPageComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/reset-password/reset-password-page.component').then(m => m.ResetPasswordPageComponent),
      },
      {
        // Aceptación de invitación (M04). El correo enviaba aquí y la ruta no existía: quien
        // pulsaba el botón del correo veía un 404, con el endpoint del backend ya escrito y sin
        // pantalla que lo llamara. La página vive en `libs/nexus` porque el resto de páginas de
        // autenticación son de PartnerCore.
        path: 'accept-invitation',
        // accept-invitation: diccionario de NEXUS. Cuelga de `auth`, no de `nexus`, así que lo
        // declara ella misma (mismo caso que `webinar`/`portal` más abajo).
        providers: [nexusScope()],
        loadComponent: () => import('@nexus/feature-teams/users/accept-invitation/accept-invitation.component').then(m => m.AcceptInvitationComponent),
      },
    ],
  },
  // Tablero de puntuación OKR compartido (M08.03 RF-025) — quien abre el enlace es un
  // stakeholder sin cuenta en el producto, así que vive fuera del layout autenticado igual que
  // las notas de versión. El token es lo único que aísla el tablero, y caduca a los 30 días.
  {
    path: 'scorecard/public/:token',
    loadComponent: () => import('@nexus/feature-okr/scorecard/public/public-scorecard.component').then(m => m.PublicScorecardComponent),
  },
  // Release notes públicas (M15.4) — fuera del panel admin, sin sidebar, sin autenticación.
  {
    path: 'releases/:token',
    loadComponent: () => import('@nexus/feature-devops/devops/components/public-release-notes/public-release-notes.component').then(m => m.PublicReleaseNotesComponent),
  },
  // Formulario público (M03.5 §3.9) — quien lo rellena es un proveedor o un cliente que no tiene
  // cuenta, así que vive fuera del layout autenticado, igual que las notas de versión. La ruta es
  // corta a propósito: se reparte por correo y se imprime en códigos QR.
  {
    path: 'f/:slug',
    loadComponent: () => import('@nexus/feature-visualizations/lib/components/public-form/public-form.component').then(m => m.PublicFormComponent),
  },
  // Inscripción pública a un webinar (M17.5 RF-002) — quien abre el enlace es alguien de fuera
  // del tenant, invitado por correo o por redes, así que vive fuera del layout autenticado igual
  // que el formulario público. El slug no es adivinable: es lo único que aísla el evento.
  {
    path: 'webinar/:slug',
    // webinar: diccionario de NEXUS. No cuelga de la ruta `nexus`, así que lo declara ella misma.
    providers: [nexusScope()],
    loadComponent: () => import('@nexus/feature-messaging/messaging/components/public-webinar/public-webinar.component').then(m => m.PublicWebinarComponent),
  },
  {
    path: 'webinar-unsubscribe/:token',
    // RNF-007/GDPR: a donde apunta el enlace de baja del correo de grabación (M17.5).
    providers: [nexusScope()],
    loadComponent: () => import('@nexus/feature-messaging/messaging/components/webinar-unsubscribe/webinar-unsubscribe.component').then(m => m.WebinarUnsubscribeComponent),
  },
  // Portal de autoservicio (M14.4) — la superficie del cliente que recibe el soporte, que por
  // definición no tiene cuenta en la plataforma. Dos formas: con token llega desde el enlace del
  // correo y lo canjea; sin token sirve para volver tras recargar, mientras la sesión siga viva.
  {
    path: 'portal/:token',
    // portal: diccionario de NEXUS. No cuelga de la ruta `nexus`, así que lo declara ella misma.
    providers: [nexusScope()],
    loadComponent: () => import('@nexus/feature-helpdesk/portal/portal-page.component').then(m => m.PortalPageComponent),
  },
  {
    path: 'portal',
    // portal: diccionario de NEXUS. No cuelga de la ruta `nexus`, así que lo declara ella misma.
    providers: [nexusScope()],
    loadComponent: () => import('@nexus/feature-helpdesk/portal/portal-page.component').then(m => m.PortalPageComponent),
  },
  // Hub de Entregables (M26.3) — mismo criterio que el Portal de helpdesk: el Contacto del
  // cliente que canjea el enlace no tiene cuenta en NEXUS PM, así que vive fuera del layout
  // autenticado. Con token llega desde el correo y lo canjea; sin token vuelve tras recargar.
  {
    path: 'entregables/portal/:token',
    providers: [nexusScope()],
    loadComponent: () => import('@nexus/feature-deliverables/portal/deliverable-portal-page.component').then(m => m.DeliverablePortalPageComponent),
  },
  {
    path: 'entregables/portal',
    providers: [nexusScope()],
    loadComponent: () => import('@nexus/feature-deliverables/portal/deliverable-portal-page.component').then(m => m.DeliverablePortalPageComponent),
  },
  // Página de estado (M20.3 RF-009 … RF-011, RF-024) — INC-0088 §9, §10, §11, §24. El cliente
  // que la mira no tiene cuenta en NEXUS PM, así que vive fuera del layout autenticado igual que
  // el portal de helpdesk y el hub de entregables. Cuatro accesos: la página por su dirección
  // (slug), y los tres que llegan desde un enlace del correo. El modo lo decide `data.modo`.
  {
    path: 'estado/:slug',
    providers: [nexusScope()],
    loadComponent: () => import('@nexus/feature-itsm/status/status-page-public.component').then(m => m.StatusPagePublicComponent),
  },
  {
    path: 'estado/confirmar/:token',
    providers: [nexusScope()],
    data: { modo: 'confirmar' },
    loadComponent: () => import('@nexus/feature-itsm/status/status-page-public.component').then(m => m.StatusPagePublicComponent),
  },
  {
    path: 'estado/baja/:token',
    providers: [nexusScope()],
    data: { modo: 'baja' },
    loadComponent: () => import('@nexus/feature-itsm/status/status-page-public.component').then(m => m.StatusPagePublicComponent),
  },
  {
    path: 'estado/preferencias/:token',
    providers: [nexusScope()],
    data: { modo: 'preferencias' },
    loadComponent: () => import('@nexus/feature-itsm/status/status-page-public.component').then(m => m.StatusPagePublicComponent),
  },
  // Páginas compartidas (M26.2) — tres accesos: link_only ("ver/:token", sin sesión),
  // authenticated_client con token nuevo ("portal/:token", canjea y guarda sesión) y
  // authenticated_client de vuelta ("portal", sesión ya guardada). El modo lo decide `data.mode`.
  {
    path: 'paginas-compartidas/ver/:token',
    providers: [nexusScope()],
    data: { mode: 'link' },
    loadComponent: () => import('@nexus/feature-shared-pages/portal/shared-page-public.component').then(m => m.SharedPagePublicComponent),
  },
  {
    path: 'paginas-compartidas/portal/:token',
    providers: [nexusScope()],
    data: { mode: 'auth' },
    loadComponent: () => import('@nexus/feature-shared-pages/portal/shared-page-public.component').then(m => m.SharedPagePublicComponent),
  },
  {
    path: 'paginas-compartidas/portal',
    providers: [nexusScope()],
    data: { mode: 'auth' },
    loadComponent: () => import('@nexus/feature-shared-pages/portal/shared-page-public.component').then(m => m.SharedPagePublicComponent),
  },
  // Invitado externo de un espacio (M01.01 RF-005). Mismo criterio que el Portal y que el guest
  // de canal compartido: quien abre el enlace no tiene cuenta, así que la pantalla vive fuera del
  // layout autenticado — enseñarle el armazón del producto con todo apagado sería ofrecerle
  // veinte puertas cerradas.
  {
    path: 'espacios/invitacion/:token',
    providers: [nexusScope()],
    loadComponent: () => import('@nexus/feature-spaces/spaces/guest/space-guest.component').then(m => m.SpaceGuestComponent),
  },
  // External Guest de un canal compartido (M05.5 DEC-002) — mismo criterio que el Portal:
  // quien abre el enlace no tiene cuenta en NEXUS PM, así que vive fuera del layout autenticado.
  // Error
  {
    path: '404',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
  {
    path: '**',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
];
